import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  FACTORY_USB_STATUS_LABEL_KEYS,
  FactoryUsbTable,
  type FactoryUsbAction,
} from '@/components/app/factory-usb-table';
import { FactoryUsbDownloadCard } from '@/components/app/factory-usb-download-card';
import { FactoryUsbProvisionWizard } from '@/components/app/factory-usb-provision-wizard';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api';
import {
  assignResponseSchema,
  factoryUsbKeysSchema,
  factoryUsbStatusFilterSchema,
  type AssignRequest,
  type AssignResponse,
  type FactoryUsbKey,
  type FactoryUsbStatusFilter,
} from '@/schemas/factory-usb';

/** Breadcrumb consumed by the router shell. */
export const handle = { crumb: 'Factory USB' };

const KEYS_QUERY_KEY = 'factory-usb-keys';
const ALL_STATUSES = 'all';
const ACTION_PARAM = 'new';

/** Options come from the contract itself, so a status added to the API cannot be forgotten here. */
const STATUS_FILTER_OPTIONS: readonly FactoryUsbStatusFilter[] =
  factoryUsbStatusFilterSchema.options;

async function fetchFactoryKeys(status?: FactoryUsbStatusFilter): Promise<FactoryUsbKey[]> {
  const query = status ? `?status=${status}` : '';
  const res = await apiFetch(`/factory-mode/keys${query}`);
  return factoryUsbKeysSchema.parse(await res.json());
}

/** JSON body for an operator POST — CSRF is added by `apiFetch` itself. */
function jsonPost(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

/** Revoke and retire are terminal; the session revoke leaves the key ACTIVE. */
async function runFactoryAction(action: FactoryUsbAction, key: FactoryUsbKey): Promise<void> {
  if (action === 'revoke') {
    await apiFetch('/factory-mode/revoke', jsonPost({ key_id: key.keyId }));
    return;
  }
  if (action === 'retire') {
    await apiFetch('/factory-mode/retire', jsonPost({ key_id: key.keyId }));
    return;
  }
  await apiFetch('/factory-mode/session/revoke', jsonPost({ key_id: key.keyId }));
}

/** Assignment of an INACTIVE key to a person — the only way a key becomes usable. */
async function assignFactoryKey(request: AssignRequest): Promise<AssignResponse> {
  const res = await apiFetch('/factory-mode/assign', jsonPost(request));
  return assignResponseSchema.parse(await res.json());
}

function RegistrySkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('factoryUsb.loading')}>
      {[0, 1, 2, 3, 4].map((row) => (
        <Skeleton key={row} className="h-12 w-full" />
      ))}
    </div>
  );
}

/**
 * Operator page for Factory USB (epic &167): the key registry plus the issuing wizard.
 * Everything here is administrator-only — the registry is the accountability record.
 */
export default function FactoryUsbPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatus = searchParams.get('status');
  const parsedStatus = factoryUsbStatusFilterSchema.safeParse(rawStatus);
  const status = parsedStatus.success ? parsedStatus.data : undefined;
  const wizardOpen = searchParams.get('action') === ACTION_PARAM;

  const keysQuery = useQuery({
    queryKey: [KEYS_QUERY_KEY, status ?? null],
    queryFn: () => fetchFactoryKeys(status),
    staleTime: 30_000,
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, key }: { action: FactoryUsbAction; key: FactoryUsbKey }) =>
      runFactoryAction(action, key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEYS_QUERY_KEY] }),
  });

  const handleAction = useCallback(
    (action: FactoryUsbAction, key: FactoryUsbKey) => actionMutation.mutateAsync({ action, key }),
    [actionMutation]
  );

  const assignMutation = useMutation({
    mutationFn: (request: AssignRequest) => assignFactoryKey(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEYS_QUERY_KEY] }),
  });

  const handleAssign = useCallback(
    (request: AssignRequest) => assignMutation.mutateAsync(request),
    [assignMutation]
  );

  const setStatusFilter = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value === ALL_STATUSES) next.delete('status');
      else next.set('status', value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setWizardOpen = useCallback(
    (open: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (open) next.set('action', ACTION_PARAM);
      else next.delete('action');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const keys = keysQuery.data ?? [];
  const hasFilter = status !== undefined;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t('factoryUsb.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('factoryUsb.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={status ?? ALL_STATUSES} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-52" aria-label={t('factoryUsb.filterLabel')}>
              <SelectValue placeholder={t('factoryUsb.filterAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>{t('factoryUsb.filterAll')}</SelectItem>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(FACTORY_USB_STATUS_LABEL_KEYS[option])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            disabled={keysQuery.isFetching}
            onClick={() => void keysQuery.refetch()}
          >
            {t('factoryUsb.refresh')}
          </Button>

          <Button onClick={() => setWizardOpen(true)}>{t('factoryUsb.issue')}</Button>
        </div>
      </header>

      <FactoryUsbDownloadCard />

      {keysQuery.isLoading ? (
        <RegistrySkeleton />
      ) : keysQuery.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/40 p-10 text-center">
          <p className="font-medium">{t('factoryUsb.loadError')}</p>
          <Button variant="outline" size="sm" onClick={() => void keysQuery.refetch()}>
            {t('factoryUsb.retry')}
          </Button>
        </div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-10 text-center">
          <p className="font-medium">
            {t(hasFilter ? 'factoryUsb.emptyFilteredTitle' : 'factoryUsb.emptyTitle')}
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            {hasFilter
              ? t('factoryUsb.emptyFilteredDescription', {
                  status: status ? t(FACTORY_USB_STATUS_LABEL_KEYS[status]) : '',
                })
              : t('factoryUsb.emptyDescription')}
          </p>
          {hasFilter ? (
            <Button variant="outline" size="sm" onClick={() => setStatusFilter(ALL_STATUSES)}>
              {t('factoryUsb.emptyFilteredReset')}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <FactoryUsbTable keys={keys} onAction={handleAction} onAssign={handleAssign} />
          <p className="text-xs text-muted-foreground">
            {t('factoryUsb.showing', { start: 1, end: keys.length, total: keys.length })}
          </p>
        </>
      )}

      <FactoryUsbProvisionWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onIssued={() => {
          void queryClient.invalidateQueries({ queryKey: [KEYS_QUERY_KEY] });
        }}
      />
    </div>
  );
}
